'use client'

import { cn } from '@morpho-org/ui-v6/utils'
import { Button, Popover, PopoverContent, PopoverTrigger } from '@repo/ui'
import { type ForwardedRef, forwardRef, type PropsWithChildren, useState } from 'react'

export interface WarningPopoverProps extends PropsWithChildren {
  /** Header text displayed at the top of the popover */
  headerLabel: string
  /** Optional body text displayed below the header */
  body?: string
  /** Label for the primary CTA button */
  ctaLabel: string
  /** Action to perform when CTA button is clicked */
  ctaAction?: () => void
  /** Label for the cancel button */
  cancelLabel?: string
  /** Additional class names for the popover content */
  className?: string
}

const WarningPopover = (
  {
    children,
    headerLabel,
    body,
    ctaLabel,
    ctaAction,
    cancelLabel = 'Cancel',
    className
  }: WarningPopoverProps,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        ref={ref}
        side="top"
        sideOffset={8}
        className={cn('rounded-lg p-md pointer-events-auto', className)}
      >
        <div className="gap-sm flex flex-col">
          <span className="text-md text-foreground text-center">{headerLabel}</span>
          {body && (
            <span className="text-2xs text-secondary-foreground px-2.5 text-center">{body}</span>
          )}
          <Button onClick={() => (ctaAction ? ctaAction() : setIsOpen(false))}>{ctaLabel}</Button>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            {cancelLabel}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

const ForwardedWarningPopover = forwardRef(WarningPopover)
ForwardedWarningPopover.displayName = 'WarningPopover'

export { ForwardedWarningPopover as WarningPopover }
